import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { usePostHog } from '../widgets/PostHogProvider';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title?: string;
  faqs: FAQ[];
  maxWidth?: number;
}

export function FAQSection({
  title = "Frequently Asked Questions",
  faqs,
  maxWidth = 800,
}: FAQSectionProps) {
  const { capture } = usePostHog();

  const handleAccordionChange = (question: string, expanded: boolean) => {
    if (expanded) {
      capture('faq_expanded', {
        question,
        section_title: title,
      });
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ maxWidth, mx: 'auto' }}>
        {faqs.map((faq, index) => (
          <Accordion
            key={index}
            elevation={0}
            onChange={(_, expanded) => handleAccordionChange(faq.question, expanded)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              mb: 1.5,
              '&:before': { display: 'none' },
              borderRadius: '12px !important',
              overflow: 'hidden',
              transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: 'primary.light',
              },
              '&.Mui-expanded': {
                borderColor: 'primary.main',
                boxShadow: '0 4px 20px rgba(77, 101, 255, 0.1)',
                mb: 2,
              },
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: 'primary.main',
                    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    '.Mui-expanded &': {
                      transform: 'rotate(180deg)',
                    },
                  }}
                />
              }
              sx={{
                fontWeight: 500,
                py: 1,
                '&.Mui-expanded': {
                  bgcolor: 'rgba(77, 101, 255, 0.04)',
                },
              }}
            >
              <Typography variant="h5" component="h3">{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Container>
  );
}